import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Plus, Loader2, Clapperboard, Download, ChevronDown, ChevronUp, ChevronRight, X, FileUp, Trash2, RefreshCw, Pencil, Copy, Archive, ArchiveRestore, LayoutTemplate, Save, Sparkles, BellRing } from "lucide-react";
import { API, authHeaders, fileUrl, uploadAdminFile, JOB_STATUS_STYLES, JOB_STATUS_LABELS, ROLE_BADGE_STYLES } from "@/constants/roles";
import { AudioPlayer } from "@/components/dashboard/AudioPlayer";
import { AnimatorAccessPicker } from "@/components/admin/AnimatorAccessPicker";
import { usePerms } from "@/components/PermsContext";
import { fmtMoney, fmtNum } from "@/utils/currency";

const JOB_ROLES = ["Standard Animator", "High-Quality Animator", "Script Writer", "Video Editor"];
const ANIMATOR_TIERS = ["Standard Animator", "High-Quality Animator"];
const PRIORITIES = ["normal", "high", "urgent"];
const STATUS_FILTERS = ["all", "draft", "available", "active", "awaiting_review", "in_revision", "completed", "archived"];

// owner/PM attention views over claimed, in-progress jobs (communication workflow only)
const ATTENTION_FILTERS = [
  { id: "due_24h", label: "Due ≤ 24h" },
  { id: "due_soon", label: "Due soon (3d)" },
  { id: "overdue", label: "Overdue" },
  { id: "no_update", label: "No recent update" },
];
const ACTIVE_STATUSES = ["active", "in_revision", "awaiting_review"];
const daysUntil = (d) => {
  if (!d) return null;
  const t = new Date(); t.setHours(0, 0, 0, 0);
  const dd = new Date(d); dd.setHours(0, 0, 0, 0);
  return (dd - t) / 86400000;
};
const matchesAttention = (j, kind) => {
  if (!ACTIVE_STATUSES.includes(j.status) || j.archived === true) return false;
  const du = daysUntil(j.deadline);
  if (kind === "due_24h") return du !== null && du >= 0 && du <= 1;
  if (kind === "due_soon") return du !== null && du >= 0 && du <= 3;
  if (kind === "overdue") return du !== null && du < 0;
  if (kind === "no_update") {
    return j.status === "active" && !(j.submissions || []).length && j.claimed_at &&
      (Date.now() - new Date(j.claimed_at).getTime()) > 48 * 3600 * 1000;
  }
  return false;
};
const nudgeAgo = (iso) => {
  const h = Math.floor((Date.now() - new Date(iso).getTime()) / 3600000);
  return h < 1 ? "just now" : h < 24 ? `${h}h ago` : `${Math.floor(h / 24)}d ago`;
};

const PRIORITY_STYLES = {
  normal: "bg-[var(--pv-raised)] text-[var(--pv-text-2)] border-[var(--pv-border)]",
  high: "bg-amber-500/15 text-amber-300 border-amber-500/30",
  urgent: "bg-rose-500/15 text-rose-300 border-rose-500/30",
};

const EMPTY_FORM = {
  title: "",
  description: "",
  script: "",
  video_duration: "",
  assets: "",
  instructions: "",
  deadline: "",
  deadline_days: 7,
  priority: "normal",
};

const formatError = (detail) => {
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => e?.msg || String(e)).join(" ");
  return "Something went wrong.";
};

const paySummary = (j) => {
  const pbr = j.pay_by_role || {};
  const parts = (j.eligible_roles || [j.role_required]).map((r) => `${fmtMoney(pbr[r] ?? j.estimated_pay, 0)} ${r === "High-Quality Animator" ? "HQ" : r === "Standard Animator" ? "STD" : r}`);
  return parts.join(" · ");
};

export const AdminProduction = () => {
  const { has, role } = usePerms();
  const [jobs, setJobs] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [filter, setFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [eligible, setEligible] = useState(["Standard Animator"]);
  const [payByRole, setPayByRole] = useState({ "Standard Animator": "55" });
  const [references, setReferences] = useState([""]);
  const [audioFile, setAudioFile] = useState(null);
  const [keepAudio, setKeepAudio] = useState("");
  const [extraFiles, setExtraFiles] = useState([]);
  const [keepFiles, setKeepFiles] = useState([]);
  const [saving, setSaving] = useState(false);
  const [expanded, setExpanded] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [templateName, setTemplateName] = useState("");
  const [busyId, setBusyId] = useState(null);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [bulkMode, setBulkMode] = useState(false);
  const [bulkEntries, setBulkEntries] = useState([{ title: "", script: "" }]);
  const [generating, setGenerating] = useState(false);
  const [editingStatus, setEditingStatus] = useState(null);
  const [adjusting, setAdjusting] = useState(false);
  const [access, setAccess] = useState("open"); // animator targeting — owner-only control
  const [selectedAnimators, setSelectedAnimators] = useState([]);
  const navigate = useNavigate();

  const load = useCallback(() => {
    axios.get(`${API}/admin/jobs`, { headers: authHeaders() })
      .then((r) => setJobs(r.data))
      .catch((err) => {
        if (err.response?.status === 401 || err.response?.status === 403) navigate("/login");
        else toast.error("Could not load jobs.");
      });
    axios.get(`${API}/admin/templates`, { headers: authHeaders() }).then((r) => setTemplates(r.data)).catch(() => {});
  }, [navigate]);

  useEffect(load, [load]);

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const toggleEligible = (role) => {
    setEligible((cur) => {
      const next = cur.includes(role) ? cur.filter((r) => r !== role) : [...cur, role];
      if (next.length === 0) return cur;
      setPayByRole((p) => {
        const copy = { ...p };
        next.forEach((r) => { if (!(r in copy)) copy[r] = r === "High-Quality Animator" ? "110" : r === "Standard Animator" ? "55" : String(p[r] || "20"); });
        Object.keys(copy).forEach((r) => { if (!next.includes(r)) delete copy[r]; });
        return copy;
      });
      return next;
    });
  };

  const resetForm = () => {
    setForm(EMPTY_FORM);
    setEligible(["Standard Animator"]);
    setPayByRole({ "Standard Animator": "55" });
    setReferences([""]);
    setAudioFile(null);
    setKeepAudio("");
    setExtraFiles([]);
    setKeepFiles([]);
    setEditingId(null);
    setTemplateName("");
    setShowAdvanced(false);
    setBulkMode(false);
    setBulkEntries([{ title: "", script: "" }]);
    setAccess("open");
    setSelectedAnimators([]);
  };

  // rapid session creation: keep the shared setup (eligibility/pay/deadline), clear per-video fields
  const resetForAnother = () => {
    setForm((f) => ({ ...EMPTY_FORM, deadline: f.deadline, deadline_days: f.deadline_days, priority: f.priority }));
    setReferences([""]);
    setAudioFile(null);
    setKeepAudio("");
    setExtraFiles([]);
    setKeepFiles([]);
    setEditingId(null);
    setShowAdvanced(false);
    setAccess("open");
    setSelectedAnimators([]);
  };

  // bulk entry helpers — each entry becomes its own draft; completely empty rows are ignored
  const setBulkEntry = (i, key, value) =>
    setBulkEntries((rows) => rows.map((r, x) => (x === i ? { ...r, [key]: value } : r)));
  const addBulkEntry = () => setBulkEntries((rows) => [...rows, { title: "", script: "" }]);
  const removeBulkEntry = (i) => setBulkEntries((rows) => (rows.length > 1 ? rows.filter((_, x) => x !== i) : rows));
  const bulkReady = bulkEntries.filter((r) => r.script.trim().length > 0);
  const bulkTooShort = bulkReady.filter((r) => r.script.trim().length < 10);

  const generateTitle = async (scriptOverride) => {
    const script = (scriptOverride ?? form.script).trim();
    if (script.length < 10) {
      toast.error("Paste the script first — the title is generated from it.");
      return;
    }
    setGenerating(true);
    try {
      const { data } = await axios.post(`${API}/admin/jobs/suggest-title`, { script }, { headers: authHeaders() });
      setForm((f) => ({ ...f, title: data.title }));
      if (data.source === "fallback") toast.info("AI unavailable — used the first line as the title. Edit as needed.");
    } catch {
      toast.error("Could not generate a title — type one manually.");
    } finally {
      setGenerating(false);
    }
  };

  // auto-suggest a title once the script is pasted, only while the title is still empty
  const onScriptBlur = () => {
    if (!editingId && !form.title.trim() && form.script.trim().length >= 10) generateTitle();
  };

  const openEdit = (j) => {
    setEditingId(j.id);
    setEditingStatus(j.status);
    setForm({
      title: j.title, description: j.description || "", script: j.script || "",
      video_duration: j.video_duration || "", assets: j.assets || "", instructions: j.instructions || "",
      deadline: j.deadline || "", deadline_days: j.deadline_days || 7, priority: j.priority || "normal",
    });
    const roles = j.eligible_roles || [j.role_required];
    setEligible(roles);
    const pbr = {};
    roles.forEach((r) => { pbr[r] = String((j.pay_by_role || {})[r] ?? j.estimated_pay); });
    setPayByRole(pbr);
    setReferences(j.references?.length ? j.references : [""]);
    setKeepAudio(j.audio_path || "");
    setKeepFiles(j.additional_files || []);
    setBulkMode(false);
    setAccess(j.access === "selected" || j.access === "direct" ? j.access : "open");
    setSelectedAnimators(j.selected_animators || []);
    setShowAdvanced(Boolean((j.description || "").trim() || (j.instructions || "").trim() || (j.video_duration || "").trim() || (j.priority && j.priority !== "normal")));
    setShowForm(true);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const applyTemplate = (id) => {
    const t = templates.find((x) => x.id === id);
    if (!t) return;
    setForm((f) => ({
      ...f,
      description: t.description || "", script: t.script || "", video_duration: t.video_duration || "",
      assets: t.assets || "", instructions: t.instructions || "", priority: t.priority || "normal",
      deadline_days: t.deadline_days || 7,
    }));
    if (t.eligible_roles?.length) {
      setEligible(t.eligible_roles);
      const pbr = {};
      t.eligible_roles.forEach((r) => { pbr[r] = String((t.pay_by_role || {})[r] ?? (r === "High-Quality Animator" ? 110 : 55)); });
      setPayByRole(pbr);
    }
    if (t.references?.length) setReferences(t.references);
    toast.success(`Template "${t.name}" applied — fill in the job-specific details.`);
  };

  const saveAsTemplate = async () => {
    if (templateName.trim().length < 3) {
      toast.error("Give the template a name first.");
      return;
    }
    try {
      await axios.post(`${API}/admin/templates`, {
        name: templateName.trim(),
        eligible_roles: eligible,
        pay_by_role: Object.fromEntries(Object.entries(payByRole).map(([k, v]) => [k, parseFloat(v) || 0])),
        description: form.description, script: form.script, video_duration: form.video_duration,
        assets: form.assets, instructions: form.instructions, references: references.filter((r) => r.trim()),
        priority: form.priority, deadline_days: parseInt(form.deadline_days) || 7,
      }, { headers: authHeaders() });
      toast.success("Template saved.");
      setTemplateName("");
      load();
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail));
    }
  };

  // admin deadline control: nudge the current deadline by +/- days (extensions) — works on claimed jobs too
  const adjustDeadline = async (days) => {
    if (!editingId) return;
    setAdjusting(true);
    try {
      const { data } = await axios.post(`${API}/admin/jobs/${editingId}/deadline`, { adjust_days: days }, { headers: authHeaders() });
      setForm((f) => ({ ...f, deadline: data.deadline }));
      toast.success(`Deadline ${days > 0 ? "extended" : "moved up"} to ${data.deadline}${data.overdue ? " (now overdue)" : ""} — the worker's countdown updated immediately.`);
      load();
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail));
    } finally {
      setAdjusting(false);
    }
  };

  const submit = async (e, addAnother = false) => {
    e.preventDefault();
    setSaving(true);
    try {
      let audio_path = keepAudio;
      if (audioFile) {
        const up = await uploadAdminFile(audioFile);
        audio_path = up.path;
      }
      const additional_files = [...keepFiles];
      for (const f of extraFiles) {
        const up = await uploadAdminFile(f);
        additional_files.push(up.path);
      }
      const payload = {
        ...form,
        eligible_roles: eligible,
        pay_by_role: Object.fromEntries(Object.entries(payByRole).map(([k, v]) => [k, parseFloat(v) || 0])),
        estimated_pay: parseFloat(payByRole[eligible[0]]) || 0,
        audio_path,
        additional_files,
        references: references.filter((r) => r.trim()),
      };
      // animator targeting: owner-only (backend enforces); managers never send these fields,
      // so a manager editing a targeted job leaves its access untouched. Targeting can only
      // change while the job is unclaimed (draft/available) — direct jobs are claimed instantly.
      const targetingEditable = !editingId || ["draft", "available"].includes(editingStatus);
      if (role === "admin" && targetingEditable) {
        if (access === "selected" && selectedAnimators.length === 0) {
          toast.error("Pick at least one animator, or switch the job back to open access.");
          setSaving(false);
          return;
        }
        if (!editingId && access === "direct" && selectedAnimators.length !== 1) {
          toast.error("Direct assignment needs exactly one animator.");
          setSaving(false);
          return;
        }
        payload.access = access;
        payload.selected_animators = access === "open" ? [] : selectedAnimators;
      }
      if (editingId) {
        // pay is owner-only on edits — managers without change_pay send operational fields only
        if (!has("change_pay")) {
          delete payload.pay_by_role;
          delete payload.estimated_pay;
        }
        await axios.patch(`${API}/admin/jobs/${editingId}`, payload, { headers: authHeaders() });
        toast.success("Job updated.");
        resetForm();
        setShowForm(false);
      } else {
        await axios.post(`${API}/admin/jobs`, payload, { headers: authHeaders() });
        if (addAnother) {
          toast.success(has("publish_jobs") ? "Job published — fresh form ready for the next one." : "Draft saved — fresh form ready for the next one.");
          resetForAnother();
          document.querySelector('[data-testid="job-script-input"]')?.focus();
        } else {
          toast.success(has("publish_jobs") ? "Job created — it's now available to claim." : "Job draft saved — the owner will review and publish it.");
          resetForm();
          setShowForm(false);
        }
      }
      load();
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const submitBulk = async (e) => {
    e.preventDefault();
    if (bulkReady.length === 0) {
      toast.error("Add a script to at least one job entry.");
      return;
    }
    if (bulkTooShort.length > 0) {
      toast.error("Every entry's script needs at least a few words — check the highlighted rows.");
      return;
    }
    if (bulkReady.length > 20) {
      toast.error("Maximum 20 jobs per batch.");
      return;
    }
    setSaving(true);
    try {
      const { data } = await axios.post(`${API}/admin/jobs/bulk`, {
        items: bulkReady.map((r) => ({ script: r.script, title: r.title.trim() || undefined })),
        eligible_roles: eligible,
        pay_by_role: Object.fromEntries(Object.entries(payByRole).map(([k, v]) => [k, parseFloat(v) || 0])),
        estimated_pay: parseFloat(payByRole[eligible[0]]) || 0,
        deadline: form.deadline,
        video_duration: form.video_duration,
        instructions: form.instructions,
      }, { headers: authHeaders() });
      toast.success(`${data.created} draft job(s) created — attach audio, confirm details, then publish each one.`);
      resetForm();
      setShowForm(false);
      setFilter("draft");
      load();
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const publish = async (j) => {
    setBusyId(j.id);
    try {
      await axios.patch(`${API}/admin/jobs/${j.id}`, { status: "available" }, { headers: authHeaders() });
      toast.success("Job published — workers can claim it now.");
      load();
    } catch (err) { toast.error(formatError(err.response?.data?.detail)); } finally { setBusyId(null); }
  };

  const [nudgeBusy, setNudgeBusy] = useState(null);

  const nudge = async (j) => {
    setNudgeBusy(j.id);
    try {
      await axios.post(`${API}/admin/jobs/${j.id}/nudge`, {}, { headers: authHeaders() });
      toast.success(`Reminder sent to ${j.claimed_by_name || "the worker"} — in-site notification + Production Manager Team chat.`);
      load();
    } catch (err) {
      const d = err.response?.data?.detail;
      if (d && typeof d === "object" && d.error === "nudge_cooldown") toast.info(d.message);
      else toast.error(typeof d === "string" ? d : "Could not send the nudge.");
    } finally { setNudgeBusy(null); }
  };

  const duplicate = async (j) => {
    setBusyId(j.id);
    try {
      await axios.post(`${API}/admin/jobs/${j.id}/duplicate`, {}, { headers: authHeaders() });
      toast.success("Duplicated as a clean draft — review it under Drafts and publish when ready.");
      load();
    } catch { toast.error("Could not duplicate job."); } finally { setBusyId(null); }
  };

  const archive = async (j) => {
    const flagArchived = j.archived === true && j.status !== "archived";
    const isLegacyPath = ["available", "draft"].includes(j.status) || (j.status === "archived" && !j.archived);
    if (!isLegacyPath) {
      // claimed / completed jobs: organizational flag — confirm explicitly, nothing is deleted
      const ok = window.confirm(flagArchived
        ? `Restore "${j.title}" back into the production workflow?`
        : `Archive "${j.title}"? It leaves the default production views, but the job, submissions, payments, costs and history are all preserved. You can restore it anytime from the Archived tab.`);
      if (!ok) return;
    }
    setBusyId(j.id);
    try {
      const { data } = await axios.post(`${API}/admin/jobs/${j.id}/archive`, {}, { headers: authHeaders() });
      const nowArchived = data.status === "archived" || data.archived === true;
      toast.success(nowArchived ? "Job archived — hidden from workers, no budget impact." : "Job restored to available.");
      load();
    } catch (err) { toast.error(formatError(err.response?.data?.detail)); } finally { setBusyId(null); }
  };

  const deleteJob = async (j) => {
    setBusyId(j.id);
    try {
      const { data } = await axios.delete(`${API}/admin/jobs/${j.id}`, { headers: authHeaders() });
      toast.success(`Job permanently deleted. Removed ${data.removed_payments} payment record(s), ${data.removed_submissions} submission(s). Committed budget now ${fmtMoney(data.budget.committed)}.`);
      setConfirmDelete(null);
      load();
    } catch { toast.error("Could not delete job."); } finally { setBusyId(null); }
  };

  const visible = useMemo(() => {
    if (!jobs) return [];
    // archived = organizational shelf (status "archived" for unclaimed jobs, or the archived
    // flag for claimed/completed ones) — hidden from every default/status view, only in the Archived tab
    const isArchived = (j) => j.status === "archived" || j.archived === true;
    let list = ATTENTION_FILTERS.some((f) => f.id === filter)
      ? jobs.filter((j) => matchesAttention(j, filter))
      : filter === "archived"
        ? jobs.filter(isArchived)
        : filter === "all"
          ? jobs.filter((j) => !isArchived(j))
          : jobs.filter((j) => j.status === filter && !isArchived(j));
    const q = search.trim().toLowerCase();
    if (q) list = list.filter((j) => (j.title + " " + (j.claimed_by_name || "")).toLowerCase().includes(q));
    return list;
  }, [jobs, filter, search]);

  const counts = useMemo(() => {
    const c = { all: 0, archived: 0 };
    (jobs || []).forEach((j) => {
      if (j.status === "archived" || j.archived === true) { c.archived += 1; return; }
      c[j.status] = (c[j.status] || 0) + 1;
      c.all += 1;
      ATTENTION_FILTERS.forEach((f) => { if (matchesAttention(j, f.id)) c[f.id] = (c[f.id] || 0) + 1; });
    });
    return c;
  }, [jobs]);

  const inputCls =
    "w-full rounded-2xl border border-[var(--pv-border)] bg-[var(--pv-bg)] px-4 py-3 text-sm font-medium text-[var(--pv-text)] placeholder:text-[var(--pv-text-4)] focus:border-cyan-400 focus:outline-none focus:ring-4 focus:ring-cyan-500/10 transition-all";
  const labelCls = "mb-1.5 block text-xs font-semibold text-[var(--pv-text-2)]";

  return (
    <div data-testid="admin-production">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-display text-2xl font-bold tracking-tight text-[var(--pv-text)]">Production</h1>
          <p className="mt-1 text-sm text-[var(--pv-text-4)]">Create jobs with everything workers need, and track them through the pipeline.</p>
        </div>
        <div className="flex gap-2">
          <button data-testid="production-refresh-btn" onClick={load} className="flex items-center gap-1.5 rounded-full border border-[var(--pv-border)] bg-[var(--pv-card)] px-3.5 py-2 text-xs font-semibold text-[var(--pv-text-3)] transition-colors hover:bg-[var(--pv-subtle)]">
            <RefreshCw className="h-3.5 w-3.5" /> Refresh
          </button>
          <button
            data-testid="new-job-btn"
            onClick={() => { if (showForm) { resetForm(); } setShowForm((s) => !s); }}
            className="flex items-center gap-1.5 rounded-full bg-cyan-500 px-4 py-2 text-xs font-bold text-white shadow-md shadow-cyan-500/25 transition-colors hover:bg-cyan-600"
          >
            {showForm ? <X className="h-3.5 w-3.5" /> : <Plus className="h-3.5 w-3.5" />}
            {showForm ? "Close" : "New job"}
          </button>
        </div>
      </div>

      {showForm && (
        <form data-testid="job-create-form" onSubmit={submit} className="mt-6 rounded-3xl border border-cyan-500/20 bg-[var(--pv-card)] p-6 sm:p-8">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <h2 className="font-display text-lg font-bold text-[var(--pv-text)]">{editingId ? "Edit job" : "Create a new job"}</h2>
              <p className="mt-1 text-xs text-[var(--pv-text-4)]">Everything you attach here is exactly what the worker sees when they claim it.</p>
            </div>
            {templates.length > 0 && !editingId && (
              <div className="flex items-center gap-2">
                <LayoutTemplate className="h-4 w-4 text-cyan-400" />
                <select
                  data-testid="template-select"
                  defaultValue=""
                  onChange={(e) => { if (e.target.value) applyTemplate(e.target.value); e.target.value = ""; }}
                  className="rounded-full border border-[var(--pv-border)] bg-[var(--pv-bg)] px-3.5 py-2 text-xs font-semibold text-[var(--pv-text-2)] focus:border-cyan-400 focus:outline-none"
                >
                  <option value="">From template...</option>
                  {templates.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
                </select>
              </div>
            )}
          </div>

          {!editingId && (
            <div className="mt-5 flex gap-2" data-testid="job-create-mode">
              {[["single", "Single job"], ["bulk", "Bulk — paste scripts"]].map(([id, label]) => (
                <button key={id} type="button" data-testid={`job-mode-${id}`} onClick={() => setBulkMode(id === "bulk")}
                  className={`rounded-full px-4 py-1.5 text-xs font-bold transition-colors ${((id === "bulk") === bulkMode) ? "bg-cyan-500 text-white" : "border border-[var(--pv-border)] bg-[var(--pv-subtle)] text-[var(--pv-text-3)] hover:text-cyan-400"}`}>
                  {label}
                </button>
              ))}
            </div>
          )}

          {bulkMode && !editingId ? (
            <div className="mt-6 space-y-4">
              <p className="text-xs text-[var(--pv-text-4)]">
                One entry per video — each becomes its own unpublished DRAFT with its own title and exactly the script you paste. Nothing is auto-published.
              </p>
              {bulkEntries.map((row, i) => (
                <div key={i} data-testid={`bulk-entry-${i}`} className={`rounded-2xl border bg-[var(--pv-subtle)] p-4 transition-colors ${row.script.trim().length > 0 && row.script.trim().length < 10 ? "border-amber-500/40" : "border-[var(--pv-border)]"}`}>
                  <div className="mb-3 flex items-center justify-between">
                    <p className="text-xs font-bold uppercase tracking-wider text-[var(--pv-text-3)]">Job {i + 1}</p>
                    <button
                      type="button"
                      data-testid={`bulk-remove-${i}`}
                      onClick={() => removeBulkEntry(i)}
                      disabled={bulkEntries.length === 1}
                      className="flex items-center gap-1 rounded-full border border-[var(--pv-border)] px-2.5 py-1 text-[10px] font-bold text-[var(--pv-text-4)] transition-colors hover:border-rose-500/40 hover:text-rose-400 disabled:cursor-not-allowed disabled:opacity-40"
                    >
                      <X className="h-3 w-3" /> Remove
                    </button>
                  </div>
                  <input
                    data-testid={`bulk-title-${i}`}
                    value={row.title}
                    onChange={(e) => setBulkEntry(i, "title", e.target.value)}
                    placeholder="Title — leave blank to auto-generate from the script"
                    maxLength={120}
                    className={inputCls}
                  />
                  <textarea
                    data-testid={`bulk-script-${i}`}
                    rows={6}
                    value={row.script}
                    onChange={(e) => setBulkEntry(i, "script", e.target.value)}
                    placeholder="Paste the full script here — preserved exactly as pasted."
                    className={`${inputCls} mt-2.5`}
                  />
                </div>
              ))}
              <button
                type="button"
                data-testid="bulk-add-btn"
                onClick={addBulkEntry}
                className="flex w-full items-center justify-center gap-2 rounded-2xl border-2 border-dashed border-[var(--pv-border-strong)] py-3.5 text-xs font-bold text-[var(--pv-text-3)] transition-colors hover:border-cyan-500/40 hover:text-cyan-400"
              >
                <Plus className="h-4 w-4" /> Add another job
              </button>
            </div>
          ) : (
          <div className="mt-6 grid gap-5 sm:grid-cols-2">
            <div className="sm:col-span-2">
              <label htmlFor="job-script" className={labelCls}>Script <span className="text-[var(--pv-text-4)] font-normal">— the main input; stored exactly as pasted, never rewritten</span></label>
              <textarea id="job-script" data-testid="job-script-input" rows={8} value={form.script} onChange={set("script")} onBlur={onScriptBlur} placeholder="Paste the full script here — dialogue, scene directions, everything. It's preserved verbatim." className={inputCls} />
            </div>

            <div className="sm:col-span-2">
              <label htmlFor="job-title" className={labelCls}>Job title <span className="text-[var(--pv-text-4)] font-normal">— auto-suggested from the script, always editable</span></label>
              <div className="flex gap-2">
                <input id="job-title" data-testid="job-title-input" required minLength={3} value={form.title} onChange={set("title")} placeholder="Auto-generated when you paste a script — or type your own" className={inputCls} />
                <button type="button" data-testid="job-generate-title-btn" onClick={() => generateTitle()} disabled={generating}
                  className="flex shrink-0 items-center gap-1.5 rounded-full border border-cyan-500/30 bg-cyan-500/10 px-4 py-2 text-xs font-bold text-cyan-300 transition-colors hover:bg-cyan-500/20 disabled:opacity-60">
                  {generating ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Sparkles className="h-3.5 w-3.5" />} Generate
                </button>
              </div>
            </div>

            <div>
              <label className={labelCls}>Audio file (voice + music)</label>
              {keepAudio && !audioFile && (
                <p className="mb-2 flex items-center gap-2 text-xs text-[var(--pv-text-3)]">
                  <span className="rounded-full bg-cyan-500/15 border border-cyan-500/30 px-2.5 py-0.5 text-[10px] font-bold text-cyan-300">Current audio attached</span>
                  <button type="button" onClick={() => setKeepAudio("")} className="text-rose-400 hover:underline">remove</button>
                </p>
              )}
              <label data-testid="job-audio-upload" className="flex cursor-pointer items-center gap-2 rounded-2xl border border-dashed border-[var(--pv-border-strong)] bg-[var(--pv-subtle)] px-4 py-3 text-xs font-semibold text-[var(--pv-text-3)] transition-colors hover:border-cyan-500/30">
                <FileUp className="h-4 w-4 text-cyan-400" />
                {audioFile ? audioFile.name : keepAudio ? "Replace audio file" : "Upload audio file"}
                <input type="file" accept="audio/*" className="hidden" onChange={(e) => setAudioFile(e.target.files?.[0] || null)} />
              </label>
            </div>
            <div>
              <label className={labelCls}>Additional files (assets, refs) <span className="text-[var(--pv-text-4)] font-normal">— optional</span></label>
              <label data-testid="job-files-upload" className="flex cursor-pointer items-center gap-2 rounded-2xl border border-dashed border-[var(--pv-border-strong)] bg-[var(--pv-subtle)] px-4 py-3 text-xs font-semibold text-[var(--pv-text-3)] transition-colors hover:border-cyan-500/30">
                <FileUp className="h-4 w-4 text-cyan-400" />
                {extraFiles.length ? `${extraFiles.length} new file(s)` : "Upload files (multi)"}
                <input type="file" multiple className="hidden" onChange={(e) => setExtraFiles(Array.from(e.target.files || []))} />
              </label>
              {(keepFiles.length > 0 || extraFiles.length > 0) && (
                <ul className="mt-2 space-y-1">
                  {keepFiles.map((p, i) => (
                    <li key={`k${i}`} className="flex items-center justify-between text-xs text-[var(--pv-text-4)]">
                      <span className="truncate">Existing file {i + 1}</span>
                      <button type="button" onClick={() => setKeepFiles((fs) => fs.filter((_, x) => x !== i))} className="text-rose-400"><Trash2 className="h-3 w-3" /></button>
                    </li>
                  ))}
                  {extraFiles.map((f, i) => (
                    <li key={`n${i}`} className="flex items-center justify-between text-xs text-[var(--pv-text-4)]">
                      <span className="truncate">{f.name}</span>
                      <button type="button" onClick={() => setExtraFiles((fs) => fs.filter((_, x) => x !== i))} className="text-rose-400"><Trash2 className="h-3 w-3" /></button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
            <div className="sm:col-span-2">
              <label htmlFor="job-assets" className={labelCls}>Assets & links (notes, drive links, avatar info) <span className="text-[var(--pv-text-4)] font-normal">— optional</span></label>
              <textarea id="job-assets" data-testid="job-assets-input" rows={2} value={form.assets} onChange={set("assets")} placeholder="Any asset links or asset notes the worker needs..." className={inputCls} />
            </div>
            <div className="sm:col-span-2">
              <label className={labelCls}>Reference links (style / quality targets) <span className="text-[var(--pv-text-4)] font-normal">— optional</span></label>
              {references.map((ref, i) => (
                <div key={i} className="mt-2 flex gap-2">
                  <input
                    data-testid={`job-reference-input-${i}`}
                    value={ref}
                    onChange={(e) => setReferences((rs) => rs.map((r, x) => (x === i ? e.target.value : r)))}
                    placeholder="https://youtube.com/shorts/..."
                    className={inputCls}
                  />
                  {references.length > 1 && (
                    <button type="button" onClick={() => setReferences((rs) => rs.filter((_, x) => x !== i))} className="shrink-0 rounded-full bg-rose-500/10 p-2.5 text-rose-400 hover:bg-rose-500/20">
                      <X className="h-4 w-4" />
                    </button>
                  )}
                </div>
              ))}
              <button type="button" data-testid="add-reference-btn" onClick={() => setReferences((rs) => [...rs, ""])} className="mt-2 text-xs font-bold text-cyan-400 hover:text-cyan-300">
                + Add another reference
              </button>
            </div>

            <div className="sm:col-span-2 rounded-2xl border border-[var(--pv-border)] bg-[var(--pv-subtle)] p-4">
              <p className={labelCls}>Who can claim this job? (eligibility)</p>
              <div className="flex flex-wrap gap-2">
                {JOB_ROLES.map((r) => (
                  <button
                    type="button"
                    key={r}
                    data-testid={`eligible-${r.toLowerCase().replace(/[\s/-]+/g, "-")}`}
                    onClick={() => toggleEligible(r)}
                    className={`rounded-full border px-3.5 py-1.5 text-xs font-bold transition-colors ${eligible.includes(r) ? ROLE_BADGE_STYLES[r] : "border-[var(--pv-border)] bg-[var(--pv-card)] text-[var(--pv-text-4)]"}`}
                  >
                    {r}
                  </button>
                ))}
              </div>
              <div className="mt-4 grid gap-3 sm:grid-cols-2">
                {eligible.map((r) => (
                  <div key={r}>
                    <label className="mb-1 block text-[11px] font-semibold text-[var(--pv-text-3)]">{r} pay (USD){r === "High-Quality Animator" ? " — min $110/video" : r === "Standard Animator" ? " — min $55/video" : ""}</label>
                    <input
                      data-testid={`pay-input-${r.toLowerCase().replace(/[\s/-]+/g, "-")}`}
                      type="number" min="1" step="0.01" required
                      value={payByRole[r] ?? ""}
                      onChange={(e) => setPayByRole((p) => ({ ...p, [r]: e.target.value }))}
                      className={inputCls}
                    />
                  </div>
                ))}
              </div>
              {eligible.some((r) => ANIMATOR_TIERS.includes(r)) && eligible.length > 1 && (
                <p className="mt-2 text-[11px] text-[var(--pv-text-4)]">Workers only ever see their own tier's rate. Whoever claims it locks their rate as the committed cost.</p>
              )}
            </div>

            {role === "admin" && (
              <AnimatorAccessPicker
                access={access}
                selectedIds={selectedAnimators}
                onChange={(a, ids) => { setAccess(a); setSelectedAnimators(ids); }}
                eligibleRoles={eligible}
                hideDirect={!!editingId}
              />
            )}

            {editingId && ["active", "awaiting_review", "in_revision", "completed"].includes(editingStatus) ? (
              <div className="sm:col-span-2" data-testid="job-deadline-claimed">
                <label htmlFor="job-deadline" className={labelCls}>Deadline <span className="text-[var(--pv-text-4)] font-normal">— locked in at claim; change it anytime (extensions)</span></label>
                <div className="flex flex-wrap items-center gap-2">
                  <input id="job-deadline" data-testid="job-deadline-input" type="date" value={form.deadline} onChange={set("deadline")} className={`${inputCls} sm:max-w-[220px]`} />
                  {[-1, 1, 3, 7].map((d) => (
                    <button key={d} type="button" data-testid={`deadline-adjust-${d > 0 ? "plus" : "minus"}${Math.abs(d)}`} onClick={() => adjustDeadline(d)} disabled={adjusting}
                      className="rounded-full border border-cyan-500/30 bg-cyan-500/10 px-3 py-2 text-xs font-bold text-cyan-300 transition-colors hover:bg-cyan-500/20 disabled:opacity-60">
                      {d > 0 ? `+${d}` : d} day{Math.abs(d) === 1 ? "" : "s"}
                    </button>
                  ))}
                </div>
                <p className="mt-1.5 text-[11px] text-[var(--pv-text-4)]">Saving or nudging updates the worker's countdown and overdue status immediately — they'll also get a notification.</p>
              </div>
            ) : (
              <div>
                <label htmlFor="job-deadline-days" className={labelCls}>Time allowed after claim (days)</label>
                <input id="job-deadline-days" data-testid="job-deadline-days-input" type="number" min="1" max="90" value={form.deadline_days}
                  onChange={(e) => setForm((f) => ({ ...f, deadline_days: e.target.value }))} className={inputCls} />
                <p className="mt-1.5 text-[11px] text-[var(--pv-text-4)]">While unclaimed the job shows "Deadline: {parseInt(form.deadline_days) || 7} days after claim" — the exact date locks in the moment a worker claims it.</p>
              </div>
            )}

            <div className="sm:col-span-2 rounded-2xl border border-[var(--pv-border)]">
              <button type="button" data-testid="job-advanced-toggle" onClick={() => setShowAdvanced((s) => !s)} className="flex w-full items-center gap-2 px-4 py-3 text-left">
                {showAdvanced ? <ChevronDown className="h-3.5 w-3.5 text-[var(--pv-text-4)]" /> : <ChevronRight className="h-3.5 w-3.5 text-[var(--pv-text-4)]" />}
                <span className="text-xs font-bold text-[var(--pv-text-2)]">More details / Advanced</span>
                <span className="text-[10px] text-[var(--pv-text-4)]">rarely needed — permanent production rules live in Guidelines, not per job</span>
              </button>
              {showAdvanced && (
                <div className="grid gap-5 border-t border-[var(--pv-border)] p-4 sm:grid-cols-2" data-testid="job-advanced-section">
                  <div className="sm:col-span-2">
                    <label htmlFor="job-description" className={labelCls}>Description <span className="text-[var(--pv-text-4)] font-normal">— optional, usually unneeded; the script explains the video</span></label>
                    <textarea id="job-description" data-testid="job-description-input" rows={2} value={form.description} onChange={set("description")} placeholder="Leave empty for normal animation jobs" className={inputCls} />
                  </div>
                  <div className="sm:col-span-2">
                    <label htmlFor="job-instructions" className={labelCls}>Job-specific instructions <span className="text-[var(--pv-text-4)] font-normal">— optional; only for something unusual about THIS video</span></label>
                    <textarea id="job-instructions" data-testid="job-instructions-input" rows={3} value={form.instructions} onChange={set("instructions")} placeholder="Leave empty when the Guidelines cover it" className={inputCls} />
                  </div>
                  <div>
                    <label htmlFor="job-duration" className={labelCls}>Target video duration</label>
                    <input id="job-duration" data-testid="job-duration-input" value={form.video_duration} onChange={set("video_duration")} placeholder="e.g. 45-60 seconds" className={inputCls} />
                  </div>
                  <div>
                    <label htmlFor="job-priority" className={labelCls}>Priority</label>
                    <select id="job-priority" data-testid="job-priority-select" value={form.priority} onChange={set("priority")} className={inputCls}>
                      {PRIORITIES.map((p) => <option key={p} value={p}>{p}</option>)}
                    </select>
                  </div>
                </div>
              )}
            </div>
          </div>
          )}

          {bulkMode && !editingId && (
            <div className="mt-6 grid gap-5 sm:grid-cols-2">
              <div className="sm:col-span-2 rounded-2xl border border-[var(--pv-border)] bg-[var(--pv-subtle)] p-4">
                <p className={labelCls}>Shared setup for every draft in this batch</p>
                <div className="flex flex-wrap gap-2">
                  {JOB_ROLES.map((r) => (
                    <button
                      type="button"
                      key={r}
                      data-testid={`bulk-eligible-${r.toLowerCase().replace(/[\s/-]+/g, "-")}`}
                      onClick={() => toggleEligible(r)}
                      className={`rounded-full border px-3.5 py-1.5 text-xs font-bold transition-colors ${eligible.includes(r) ? ROLE_BADGE_STYLES[r] : "border-[var(--pv-border)] bg-[var(--pv-card)] text-[var(--pv-text-4)]"}`}
                    >
                      {r}
                    </button>
                  ))}
                </div>
                <div className="mt-4 grid gap-3 sm:grid-cols-2">
                  {eligible.map((r) => (
                    <div key={r}>
                      <label className="mb-1 block text-[11px] font-semibold text-[var(--pv-text-3)]">{r} pay (USD){r === "High-Quality Animator" ? " — min $110/video" : r === "Standard Animator" ? " — min $55/video" : ""}</label>
                      <input
                        data-testid={`bulk-pay-input-${r.toLowerCase().replace(/[\s/-]+/g, "-")}`}
                        type="number" min="1" step="0.01" required
                        value={payByRole[r] ?? ""}
                        onChange={(e) => setPayByRole((p) => ({ ...p, [r]: e.target.value }))}
                        className={inputCls}
                      />
                    </div>
                  ))}
                </div>
              </div>
              <div>
                <label htmlFor="job-bulk-deadline-days" className={labelCls}>Days allowed after claim (applied to all)</label>
                <input id="job-bulk-deadline-days" data-testid="job-bulk-deadline-days-input" type="number" min="1" max="90" value={form.deadline_days}
                  onChange={(e) => setForm((f) => ({ ...f, deadline_days: e.target.value }))} className={inputCls} />
              </div>
            </div>
          )}

          <div className="mt-6 flex flex-wrap items-center gap-3">
            {bulkMode && !editingId ? (
              <button
                data-testid="job-bulk-submit"
                type="button"
                onClick={submitBulk}
                disabled={saving}
                className="flex items-center gap-2 rounded-full bg-cyan-500 px-6 py-3 text-sm font-bold text-white shadow-lg shadow-cyan-500/25 transition-colors hover:bg-cyan-600 disabled:opacity-60"
              >
                {saving && <Loader2 className="h-4 w-4 animate-spin" />}
                {saving ? "Creating drafts..." : `Create ${bulkReady.length || ""} draft${bulkReady.length === 1 ? "" : "s"}`}
              </button>
            ) : (
              <>
                <button
                  data-testid="job-create-submit"
                  type="submit"
                  disabled={saving}
                  className="flex items-center gap-2 rounded-full bg-cyan-500 px-6 py-3 text-sm font-bold text-white shadow-lg shadow-cyan-500/25 transition-colors hover:bg-cyan-600 disabled:opacity-60"
                >
                  {saving && <Loader2 className="h-4 w-4 animate-spin" />}
                  {saving ? "Saving..." : editingId ? "Save changes" : has("publish_jobs") ? "Create job" : "Save as draft"}
                </button>
                {!editingId && (
                  <button
                    data-testid="job-create-another-btn"
                    type="button"
                    onClick={(e) => submit(e, true)}
                    disabled={saving}
                    className="flex items-center gap-2 rounded-full border border-cyan-500/30 bg-cyan-500/10 px-5 py-3 text-sm font-bold text-cyan-300 transition-colors hover:bg-cyan-500/20 disabled:opacity-60"
                  >
                    <Plus className="h-4 w-4" /> Create & add another
                  </button>
                )}
              </>
            )}
            {!bulkMode && (
            <div className="flex items-center gap-2">
              <input
                data-testid="template-name-input"
                value={templateName}
                onChange={(e) => setTemplateName(e.target.value)}
                placeholder="Template name"
                className="w-44 rounded-full border border-[var(--pv-border)] bg-[var(--pv-bg)] px-3.5 py-2 text-xs font-medium text-[var(--pv-text)] placeholder:text-[var(--pv-text-4)] focus:border-cyan-400 focus:outline-none"
              />
              <button type="button" data-testid="save-template-btn" onClick={saveAsTemplate} className="flex items-center gap-1.5 rounded-full border border-[var(--pv-border)] bg-[var(--pv-subtle)] px-4 py-2 text-xs font-bold text-[var(--pv-text-2)] hover:text-[var(--pv-text)]">
                <Save className="h-3.5 w-3.5" /> Save as template
              </button>
            </div>
            )}
            {editingId && (
              <button type="button" onClick={() => { resetForm(); setShowForm(false); }} className="text-xs font-bold text-[var(--pv-text-4)] hover:text-[var(--pv-text)]">Cancel editing</button>
            )}
          </div>
        </form>
      )}

      <div className="mt-8 flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
        <div className="flex flex-wrap gap-2">
          {STATUS_FILTERS.map((f) => (
            <button
              key={f}
              data-testid={`job-filter-${f}`}
              onClick={() => setFilter(f)}
              className={`rounded-full px-4 py-2 text-xs font-semibold transition-colors ${
                filter === f ? "bg-cyan-500 text-white shadow-md shadow-cyan-500/25" : "border border-[var(--pv-border)] bg-[var(--pv-card)] text-[var(--pv-text-3)] hover:border-cyan-500/30"
              }`}
            >
              {f === "all" ? "All" : f === "archived" ? "Archived" : JOB_STATUS_LABELS[f]}
              <span className="ml-1.5 opacity-70">{counts[f] || 0}</span>
            </button>
          ))}
        </div>
        <div className="relative lg:w-64">          <input
            data-testid="job-search-input"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search jobs or workers..."
            className="w-full rounded-full border border-[var(--pv-border)] bg-[var(--pv-card)] py-2.5 px-4 text-sm font-medium text-[var(--pv-text)] placeholder:text-[var(--pv-text-4)] focus:border-cyan-400 focus:outline-none focus:ring-4 focus:ring-cyan-500/10"
          />
        </div>
      </div>

      <div className="mt-3 flex flex-wrap items-center gap-2" data-testid="attention-filters">
        <span className="text-[10px] font-bold uppercase tracking-[0.2em] text-[var(--pv-text-4)]">Needs attention</span>
        {ATTENTION_FILTERS.map((f) => (
          <button
            key={f.id}
            data-testid={`job-filter-${f.id}`}
            onClick={() => setFilter(f.id)}
            className={`rounded-full px-3.5 py-1.5 text-[11px] font-bold transition-colors ${
              filter === f.id
                ? "bg-rose-500 text-white shadow-md shadow-rose-500/25"
                : "border border-[var(--pv-border)] bg-[var(--pv-card)] text-[var(--pv-text-3)] hover:border-rose-500/30"
            }`}
          >
            {f.label}
            <span className="ml-1.5 opacity-70">{counts[f.id] || 0}</span>
          </button>
        ))}
      </div>

      <div className="mt-6 space-y-4">
        {!jobs ? (
          <p data-testid="jobs-loading" className="py-16 text-center text-sm font-medium text-[var(--pv-text-4)]">Loading jobs...</p>
        ) : visible.length === 0 ? (
          <div data-testid="jobs-empty" className="flex flex-col items-center gap-3 rounded-3xl border border-dashed border-[var(--pv-border-strong)] bg-[var(--pv-card)] py-16">
            <Clapperboard className="h-10 w-10 text-[var(--pv-text-4)]" />
            <p className="text-sm font-medium text-[var(--pv-text-4)]">No jobs here. Create your first one.</p>
          </div>
        ) : (
          visible.map((j) => (
            <article key={j.id} data-testid={`job-card-${j.id}`} className="rounded-3xl border border-[var(--pv-border)] bg-[var(--pv-card)] p-6">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="flex flex-wrap items-center gap-2.5">
                    <h3 className="font-display text-lg font-semibold text-[var(--pv-text)]">{j.title}</h3>
                    {(j.eligible_roles || [j.role_required]).map((r) => (
                      <span key={r} className={`rounded-full border px-2.5 py-0.5 text-[11px] font-bold ${ROLE_BADGE_STYLES[r] || ""}`}>{r}</span>
                    ))}
                    <span data-testid={`job-status-${j.id}`} className={`rounded-full border px-2.5 py-0.5 text-[11px] font-bold uppercase ${JOB_STATUS_STYLES[j.status] || "bg-[var(--pv-raised)] text-[var(--pv-text-3)] border-[var(--pv-border)]"}`}>{JOB_STATUS_LABELS[j.status] || j.status}</span>
                    {j.archived === true && j.status !== "archived" && (
                      <span data-testid={`job-archived-chip-${j.id}`} className="rounded-full border border-slate-500/40 bg-slate-500/15 px-2.5 py-0.5 text-[11px] font-bold uppercase text-slate-300">Archived</span>
                    )}
                    {j.priority !== "normal" && (
                      <span className={`rounded-full border px-2.5 py-0.5 text-[11px] font-bold uppercase ${PRIORITY_STYLES[j.priority]}`}>{j.priority}</span>
                    )}
                    {j.access === "selected" && (
                      <span data-testid={`job-access-badge-${j.id}`} title={(j.selected_animator_names || []).join(", ")} className="rounded-full border border-violet-500/30 bg-violet-500/10 px-2.5 py-0.5 text-[11px] font-bold text-violet-300">
                        Selected only{j.selected_animator_names?.length ? ` · ${j.selected_animator_names.length}` : ""}
                      </span>
                    )}
                    {j.access === "direct" && (
                      <span data-testid={`job-access-badge-${j.id}`} className="rounded-full border border-fuchsia-500/30 bg-fuchsia-500/10 px-2.5 py-0.5 text-[11px] font-bold text-fuchsia-300">
                        Direct assignment
                      </span>
                    )}
                  </div>
                  <p className="mt-1.5 text-xs font-medium text-[var(--pv-text-4)]">
                    <span className="font-display text-base font-bold text-[var(--pv-text)]">{fmtMoney(j.estimated_pay)}</span>
                    <span className="ml-2 text-[var(--pv-text-3)]">({paySummary(j)})</span>
                    {j.video_duration && <span className="ml-3">{j.video_duration}</span>}
                    {j.deadline
                      ? <span className="ml-3">Due {new Date(j.deadline + "T00:00:00").toLocaleDateString(undefined, { month: "short", day: "numeric" })}</span>
                      : (j.deadline_days && ["draft", "available"].includes(j.status))
                        ? <span className="ml-3" data-testid={`job-days-after-claim-${j.id}`}>Deadline: {j.deadline_days} days after claim</span>
                        : null}
                    {j.claimed_by_name && <span className="ml-3">Claimed by <span className="font-bold text-[var(--pv-text)]">{j.claimed_by_name}</span></span>}
                    {j.payment_status && <span className={`ml-3 font-bold ${j.payment_status === "paid" ? "text-emerald-400" : "text-amber-400"}`}>Payment: {j.payment_status}</span>}
                  </p>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  {j.status === "draft" && has("publish_jobs") && (
                    <button data-testid={`job-publish-${j.id}`} onClick={() => publish(j)} disabled={busyId === j.id} className="flex items-center gap-1 rounded-full bg-emerald-500 px-3.5 py-1.5 text-xs font-bold text-white hover:bg-emerald-600 disabled:opacity-50">
                      {busyId === j.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : null} Publish
                    </button>
                  )}
                  {has("edit_jobs") && (
                    <button data-testid={`job-edit-${j.id}`} onClick={() => openEdit(j)} title="Edit job" className="flex items-center gap-1 rounded-full border border-[var(--pv-border)] bg-[var(--pv-subtle)] px-3.5 py-1.5 text-xs font-semibold text-[var(--pv-text-2)] hover:text-[var(--pv-text)]">
                      <Pencil className="h-3.5 w-3.5" /> Edit
                    </button>
                  )}
                  {has("create_job_drafts") && (
                    <button data-testid={`job-duplicate-${j.id}`} onClick={() => duplicate(j)} disabled={busyId === j.id} title="Duplicate job" className="flex items-center gap-1 rounded-full border border-[var(--pv-border)] bg-[var(--pv-subtle)] px-3.5 py-1.5 text-xs font-semibold text-[var(--pv-text-2)] hover:text-[var(--pv-text)] disabled:opacity-50">
                      <Copy className="h-3.5 w-3.5" /> Duplicate
                    </button>
                  )}
                  {has("edit_jobs") && j.claimed_by && ACTIVE_STATUSES.includes(j.status) && (
                    <span className="flex items-center gap-1.5">
                      <button
                        data-testid={`job-nudge-${j.id}`}
                        onClick={() => nudge(j)}
                        disabled={nudgeBusy === j.id}
                        title={j.last_nudge ? `Last nudge ${nudgeAgo(j.last_nudge.at)} by ${j.last_nudge.by_name || "the team"}` : "Send a deadline reminder to the worker"}
                        className="flex items-center gap-1 rounded-full border border-amber-500/30 bg-amber-500/10 px-3.5 py-1.5 text-xs font-semibold text-amber-300 hover:bg-amber-500/20 disabled:opacity-50"
                      >
                        {nudgeBusy === j.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <BellRing className="h-3.5 w-3.5" />}
                        Nudge worker
                      </button>
                      {j.last_nudge && (
                        <span data-testid={`job-last-nudge-${j.id}`} className="text-[10px] font-semibold text-[var(--pv-text-4)]">
                          Nudged {nudgeAgo(j.last_nudge.at)} by {j.last_nudge.by_name || "team"}
                        </span>
                      )}
                    </span>
                  )}
                  {(["available", "archived", "draft"].includes(j.status) ? has("edit_jobs") : role === "admin") && (
                    <button data-testid={`job-archive-${j.id}`} onClick={() => archive(j)} disabled={busyId === j.id} title={j.status === "archived" || j.archived ? "Restore job" : "Archive job"} className="flex items-center gap-1 rounded-full border border-[var(--pv-border)] bg-[var(--pv-subtle)] px-3.5 py-1.5 text-xs font-semibold text-[var(--pv-text-2)] hover:text-[var(--pv-text)] disabled:opacity-50">
                      {j.status === "archived" || j.archived ? <ArchiveRestore className="h-3.5 w-3.5" /> : <Archive className="h-3.5 w-3.5" />}
                      {j.status === "archived" || j.archived ? "Restore" : "Archive"}
                    </button>
                  )}
                  <button data-testid={`job-expand-${j.id}`} onClick={() => setExpanded((e) => (e === j.id ? null : j.id))} className="flex items-center gap-1 rounded-full border border-[var(--pv-border)] bg-[var(--pv-subtle)] px-3.5 py-1.5 text-xs font-semibold text-[var(--pv-text-2)] hover:text-[var(--pv-text)]">
                    {expanded === j.id ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}
                    Details
                  </button>
                  {!has("delete_records") ? null : confirmDelete === j.id ? (
                    <span className="flex items-center gap-2 rounded-full border border-rose-500/30 bg-rose-500/10 px-3 py-1.5">
                      <span className="text-[11px] font-bold text-rose-300">Delete permanently? Removes payments & submissions.</span>
                      <button data-testid={`job-delete-confirm-${j.id}`} onClick={() => deleteJob(j)} disabled={busyId === j.id} className="rounded-full bg-rose-500 px-2.5 py-1 text-[11px] font-bold text-white hover:bg-rose-600 disabled:opacity-50">
                        {busyId === j.id ? <Loader2 className="h-3 w-3 animate-spin" /> : "Yes, delete"}
                      </button>
                      <button onClick={() => setConfirmDelete(null)} className="text-[11px] font-bold text-[var(--pv-text-3)] hover:text-[var(--pv-text)]">Cancel</button>
                    </span>
                  ) : (
                    <button data-testid={`job-delete-${j.id}`} onClick={() => setConfirmDelete(j.id)} title="Delete job permanently" className="rounded-full bg-rose-500/10 p-2 text-rose-400 transition-colors hover:bg-rose-500/20">
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  )}
                </div>
              </div>

              {expanded === j.id && (
                <div data-testid={`job-details-${j.id}`} className="mt-5 space-y-4 rounded-2xl bg-[var(--pv-subtle)] p-5 text-sm">
                  {j.description && <div><p className="text-[11px] font-bold uppercase tracking-wider text-[var(--pv-text-4)]">Description</p><p className="mt-1 whitespace-pre-wrap text-[var(--pv-text-2)]">{j.description}</p></div>}
                  {j.script && <div><p className="text-[11px] font-bold uppercase tracking-wider text-[var(--pv-text-4)]">Script</p><p className="mt-1 max-h-60 overflow-y-auto whitespace-pre-wrap rounded-xl bg-[var(--pv-card)] p-4 text-[var(--pv-text-2)] border border-[var(--pv-border)]">{j.script}</p></div>}
                  {j.instructions && <div><p className="text-[11px] font-bold uppercase tracking-wider text-[var(--pv-text-4)]">Instructions</p><p className="mt-1 whitespace-pre-wrap text-[var(--pv-text-2)]">{j.instructions}</p></div>}
                  {j.assets && <div><p className="text-[11px] font-bold uppercase tracking-wider text-[var(--pv-text-4)]">Assets & links</p><p className="mt-1 whitespace-pre-wrap text-[var(--pv-text-2)]">{j.assets}</p></div>}
                  {j.references?.length > 0 && (
                    <div>
                      <p className="text-[11px] font-bold uppercase tracking-wider text-[var(--pv-text-4)]">References</p>
                      <ul className="mt-1 space-y-1">
                        {j.references.map((r, i) => (
                          <li key={i}><a href={r} target="_blank" rel="noopener noreferrer" className="text-xs font-semibold text-cyan-400 hover:text-cyan-300">{r}</a></li>
                        ))}
                      </ul>
                    </div>
                  )}
                  {(j.audio_path || j.additional_files?.length > 0) && (
                    <div>
                      <p className="text-[11px] font-bold uppercase tracking-wider text-[var(--pv-text-4)]">Files</p>
                      {j.audio_path && (
                        <div className="mt-2">
                          <AudioPlayer path={j.audio_path} filename="Job audio" testid={`job-audio-player-${j.id}`} />
                        </div>
                      )}
                      {j.additional_files?.length > 0 && (
                        <div className="mt-2 flex flex-wrap gap-2">
                          {j.additional_files?.map((p, i) => (
                            <a key={i} data-testid={`job-file-dl-${j.id}-${i}`} href={fileUrl(p)} className="flex items-center gap-1.5 rounded-full bg-[var(--pv-raised)] border border-[var(--pv-border)] px-3 py-1.5 text-xs font-bold text-[var(--pv-text-2)] hover:bg-[var(--pv-subtle)]">
                              <Download className="h-3 w-3" /> File {i + 1}
                            </a>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                  {j.submissions?.length > 0 && (
                    <div>
                      <p className="text-[11px] font-bold uppercase tracking-wider text-[var(--pv-text-4)]">Submissions ({j.submissions.length})</p>
                      <ul className="mt-2 space-y-1.5">
                        {j.submissions.map((s) => (
                          <li key={s.id} className="flex flex-wrap items-center gap-2 text-xs text-[var(--pv-text-3)]">
                            <span className="font-bold">v{s.version}</span>
                            <a href={fileUrl(s.file_path)} className="font-semibold text-cyan-400 hover:text-cyan-300">{s.filename}</a>
                            <span className={`rounded-full border px-2 py-0.5 text-[10px] font-bold uppercase ${
                              s.status === "approved" ? "bg-emerald-500/15 text-emerald-300 border-emerald-500/30"
                              : s.status === "revision_requested" ? "bg-rose-500/15 text-rose-300 border-rose-500/30"
                              : "bg-amber-500/15 text-amber-300 border-amber-500/30"
                            }`}>{s.status.replace(/_/g, " ")}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              )}
            </article>
          ))
        )}
      </div>
    </div>
  );
};
